import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, TextField, Button, Divider, Alert, Stack } from '@mui/material';
import { useAuth } from '@frontend/components/AuthProvider';
import { useTranslation } from '@frontend/i18n';

function Welcome(): JSX.Element {
  const navigate = useNavigate();
  const { user, logout, switchOrganization } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [newOrgName, setNewOrgName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [orgSignupEnabled, setOrgSignupEnabled] = useState<boolean>(true);
  const t = useTranslation();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/v1/organizations/config');
        if (!res.ok) throw new Error('Failed to load org config');
        const data = (await res.json()) as { orgSignupEnabled?: boolean };
        if (!cancelled) setOrgSignupEnabled(Boolean(data.orgSignupEnabled));
      } catch (e) {
        // default allow if config missing
        console.warn('Org config fetch failed, defaulting to enabled:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      setError('Failed to logout');
    }
  };

  const handleCreateOrganization = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newOrgName.trim()) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      const response = await fetch('/api/v1/organizations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newOrgName.trim() }),
      });

      if (!response.ok) {
        throw new Error(await response.text());
      }

      const data = (await response.json()) as { id: number };
      await switchOrganization(data.id);
      navigate('/team');
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Failed to create organization');
    } finally {
      setSubmitting(false);
    }
  };

  const handleJoinOrganization = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteCode.trim()) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      const response = await fetch('/api/v1/auth/join/' + inviteCode.trim(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        throw new Error(await response.text());
      }

      const data = (await response.json()) as { id: number };
      await switchOrganization(data.id);
      navigate('/team');
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Failed to join organization');
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    return <></>; // Let useEffect handle navigation
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
        }}
      >
        <Alert severity="error">Error: {error}</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
      }}
    >
      <Paper
        elevation={2}
        sx={{
          maxWidth: 520,
          width: '100%',
          p: 5,
          mx: 2,
          borderRadius: 3,
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography variant="h4" gutterBottom>
            {t.welcome.greeting.replace('{name}', user?.name || 'User')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, fontSize: '0.875rem' }}>
            {t.welcome.loading}
          </Typography>
          <Typography variant="body1" color="text.primary" sx={{ fontWeight: 500, lineHeight: 1.6 }}>
            {orgSignupEnabled
              ? t.welcome.getStarted
              : t.welcome.enterInviteCode}
          </Typography>
        </Box>

        {submitError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {submitError}
          </Alert>
        )}

        <Stack spacing={3.5}>
          {orgSignupEnabled && (
            <>
              <Box component="form" onSubmit={handleCreateOrganization}>
                <Typography variant="h6" gutterBottom>
                  {t.welcome.createOrganization}
                </Typography>
                <TextField
                  fullWidth
                  label={t.welcome.organizationName}
                  value={newOrgName}
                  onChange={(e) => setNewOrgName(e.target.value)}
                  disabled={submitting}
                  sx={{ mb: 2.5 }}
                  helperText={t.welcome.chooseName}
                />
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={submitting || !newOrgName.trim()}
                  size="large"
                  sx={{ py: 1.5 }}
                >
                  {submitting ? t.welcome.creating : t.welcome.create}
                </Button>
              </Box>

              <Divider sx={{ my: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Or
                </Typography>
              </Divider>
            </>
          )}

          <Box component="form" onSubmit={handleJoinOrganization}>
            <Typography variant="h6" gutterBottom>
              {t.welcome.joinOrganization}
            </Typography>
            <TextField
              fullWidth
              id="inviteCode"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value)}
              placeholder={t.welcome.inviteCode}
              disabled={submitting}
              sx={{ mb: 2.5 }}
              helperText={t.welcome.enterInviteCode}
            />
            <Button type="submit" variant="contained" fullWidth disabled={submitting || !inviteCode.trim()} size="large" sx={{ py: 1.5 }}>
              {submitting ? t.welcome.joining : t.welcome.join}
            </Button>
          </Box>

          <Box sx={{ pt: 3, borderTop: 1, borderColor: 'divider' }}>
            <Button variant="outlined" onClick={handleLogout} sx={{ mt: 2 }}>
              {t.common.logout}
            </Button>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
}

export default Welcome;
